import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  Photo as PhotoIcon,
  Assignment as AssignmentIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

interface OverviewStats {
  guests: {
    total_guests: number;
    active_guests: number;
    new_guests_week: number;
  };
  events: {
    total_events: number;
    guests_with_registrations: number;
    avg_registrations_per_event: number;
  };
  media: {
    total_media: number;
    approved_media: number;
    featured_media: number;
    new_media_week: number;
  };
  requests: {
    event_change_requests: number;
    size_change_requests: number;
    pending_requests: number;
    approved_requests: number;
  };
}

interface PendingItem {
  id: string;
  type: 'access_request' | 'event_change' | 'size_change' | 'media_approval';
  title: string;
  description: string;
  createdAt: string;
}

interface AdminOverviewProps {
  onNavigateToTab?: (tabIndex: number) => void;
}

export const AdminOverview: React.FC<AdminOverviewProps> = ({ onNavigateToTab }) => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Check if we're in demo mode
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Use demo data
        const { mockApiCall, demoData } = await import('../services/demoData');
        
        const [analyticsData, accessData, eventData, sizeData, mediaData] = await Promise.all([
          mockApiCall('/api/admin/analytics/usage'),
          mockApiCall('/api/admin/access-requests?status=pending'),
          mockApiCall('/api/admin/event-changes?status=pending'),
          mockApiCall('/api/admin/size-change-requests?status=pending'),
          mockApiCall('/api/admin/media/pending')
        ]);

        setStats(analyticsData.data);

        // Collect pending items from demo data
        const pending: PendingItem[] = [];

        accessData.requests?.forEach((request: any) => {
          pending.push({
            id: request.id,
            type: 'access_request',
            title: 'Access Request',
            description: `${request.full_name} (${request.email})`,
            createdAt: request.created_at
          });
        });

        eventData.requests?.forEach((request: any) => {
          pending.push({
            id: request.id,
            type: 'event_change',
            title: 'Event Change Request',
            description: `${request.guest_name} - Event modification request`,
            createdAt: request.created_at
          });
        });

        sizeData.data?.requests?.forEach((request: any) => {
          pending.push({
            id: request.id,
            type: 'size_change',
            title: 'Size Change Request',
            description: `${request.guest_name} - Size change request`,
            createdAt: request.created_at
          });
        });

        mediaData.data?.media?.forEach((media: any) => {
          pending.push({
            id: media.id,
            type: 'media_approval',
            title: 'Media Approval',
            description: `${media.original_name} by ${media.guest_name}`,
            createdAt: media.uploaded_at
          });
        });

        // Sort by creation date (newest first)
        pending.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setPendingItems(pending.slice(0, 10));
        
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Load analytics data from real backend
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      console.log('AdminOverview API_BASE_URL:', API_BASE_URL);
      
      const [analyticsResponse, accessRequestsResponse, changeRequestsResponse, mediaResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/analytics/usage`, { headers }),
        fetch(`${API_BASE_URL}/admin/access-requests?status=pending`, { headers }),
        fetch(`${API_BASE_URL}/admin/change-requests?status=pending`, { headers }),
        fetch(`${API_BASE_URL}/admin/media/pending`, { headers })
      ]);

      console.log('Response statuses:', {
        analytics: analyticsResponse.status,
        accessRequests: accessRequestsResponse.status,
        changeRequests: changeRequestsResponse.status,
        media: mediaResponse.status
      });

      if (!analyticsResponse.ok) {
        const errorText = await analyticsResponse.text();
        console.error('Analytics API error:', errorText);
        throw new Error(`Failed to load analytics data: ${analyticsResponse.status}`);
      }

      const analyticsData = await analyticsResponse.json();
      setStats(analyticsData.data);

      // Collect pending items
      const pending: PendingItem[] = [];

      if (accessRequestsResponse.ok) {
        const accessData = await accessRequestsResponse.json();
        accessData.requests?.forEach((request: any) => {
          pending.push({
            id: request.id,
            type: 'access_request',
            title: 'Access Request',
            description: `${request.full_name} (${request.email})`,
            createdAt: request.created_at
          });
        });
      }

      if (changeRequestsResponse.ok) {
        const changeData = await changeRequestsResponse.json();
        changeData.data?.forEach((request: any) => {
          // Skip transportation requests as they're handled in the Transportation tab
          if (request.requestType === 'transportation' || request.request_type === 'transportation') {
            return;
          }
          
          const requestType = request.requestType || request.request_type;
          const guestName = request.guestName || request.guest_name || 'Guest';
          const eventName = request.eventName || request.event_name || 'Event';
          const itemType = request.itemType || request.item_type || 'Item';
          const currentSize = request.currentSize || request.current_size || '';
          const requestedSize = request.requestedSize || request.requested_size || '';
          const createdAt = request.createdAt || request.created_at;
          
          pending.push({
            id: request.id,
            type: requestType,
            title: requestType === 'event_change' ? 'Event Change Request' : 'Size Change Request',
            description: requestType === 'event_change' 
              ? `${guestName} - ${eventName}` 
              : `${guestName} - ${itemType} (${currentSize} → ${requestedSize})`,
            createdAt: createdAt
          });
        });
      }

      if (mediaResponse.ok) {
        const mediaData = await mediaResponse.json();
        mediaData.data?.media?.forEach((media: any) => {
          pending.push({
            id: media.id,
            type: 'media_approval',
            title: 'Media Approval',
            description: `${media.original_name} by ${media.guest_name || 'Guest'}`,
            createdAt: media.uploaded_at
          });
        });
      }

      // Sort by creation date (newest first)
      pending.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setPendingItems(pending.slice(0, 10)); // Show only latest 10

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overview data');
    } finally {
      setLoading(false);
    }
  };

  const triggerMediaRotation = async () => {
    try {
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        // Simulate rotation in demo mode
        console.log('Demo mode: Simulating media rotation');
        setTimeout(() => {
          loadOverviewData();
        }, 1000);
        return;
      }

      const token = localStorage.getItem('authToken');
      const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      const response = await fetch(`${API_BASE}/api/admin/scheduler/trigger-rotation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh stats after rotation
        loadOverviewData();
      }
    } catch (err) {
      console.error('Failed to trigger media rotation:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!stats) {
    return <Alert severity="warning">No data available</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Dashboard Overview
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Guest Statistics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => onNavigateToTab?.(1)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Guests</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {stats.guests.total_guests}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.guests.active_guests} active
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.guests.new_guests_week} new this week
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Event Statistics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => onNavigateToTab?.(4)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <EventIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Events</Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {stats.events.total_events}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.events.guests_with_registrations} guests registered
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(Number(stats.events.avg_registrations_per_event) || 0)} avg per event
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Media Statistics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => onNavigateToTab?.(6)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PhotoIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Media</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {stats.media.total_media}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.media.approved_media} approved
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.media.featured_media} featured
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Request Statistics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onClick={() => onNavigateToTab?.(5)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AssignmentIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Requests</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {stats.requests.pending_requests}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                pending approval
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.requests.approved_requests} approved total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Pending Items */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <NotificationsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Pending Actions</Typography>
                </Box>
                <Chip 
                  label={pendingItems.length} 
                  color={pendingItems.length > 0 ? "warning" : "success"}
                  size="small"
                />
              </Box>
              
              {pendingItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No pending actions
                </Typography>
              ) : (
                <List dense>
                  {pendingItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <ListItem>
                        <ListItemIcon>
                          {item.type === 'access_request' && <PeopleIcon fontSize="small" />}
                          {item.type === 'event_change' && <EventIcon fontSize="small" />}
                          {item.type === 'size_change' && <AssignmentIcon fontSize="small" />}
                          {item.type === 'media_approval' && <PhotoIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          secondary={
                            <Box>
                              <Typography variant="body2" component="span">
                                {item.description}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(item.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < pendingItems.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Quick Actions</Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="outlined"
                  onClick={triggerMediaRotation}
                  startIcon={<PhotoIcon />}
                  fullWidth
                >
                  Rotate Featured Media
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => loadOverviewData()}
                  fullWidth
                >
                  Refresh Data
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};